/**
 * decree-templates.ts — model wiersza SZABLONU dekretacji ("Podpowiedz dekret" +
 * Ustawienia → Szablony dekretacji). Mirror `decree-lines.ts` (ten sam plik dla wierszy
 * KONKRETNEGO dekretu faktury) — ale wiersz szablonu niesie WZORZEC, nie kwotę:
 * `accountNumber` (string, NIE `accountId` — szablon jest przenośny między dokumentami,
 * patrz `decree_templates.lines` jsonb w `apps/api/drizzle/tenant/schema.ts`) +
 * `amountSource` ('net'|'vat'|'gross') zamiast konkretnej kwoty.
 *
 * `accountId` na `DecreeTemplateLineDraft` jest WYŁĄCZNIE pomocą UI (highlight/clear w
 * `AccountSearchPicker`) — wiersz wczytany z serwera ma `accountId: ''` (serwer nie zna id,
 * tylko numer), dopóki user nie ponownie wybierze konta przez picker w TEJ sesji edycji.
 * Payload wysyłany do API niesie WYŁĄCZNIE `accountNumber` (mirror `decree_templates.lines`
 * schema — brak kolumny accountId).
 */

export type DecreeTemplateSide = 'debit' | 'credit';
export type DecreeTemplateAmountSource = 'net' | 'vat' | 'gross';

/** Wiersz edytowalny (stan grida w `DecreeTemplateFormModal`). */
export interface DecreeTemplateLineDraft {
  /** Stabilny identyfikator klienta (React key) — NIE wysyłany do API. */
  key: string;
  groupNo: number;
  side: DecreeTemplateSide;
  /** Pomoc UI dla `AccountSearchPicker` — patrz nagłówek pliku. */
  accountId: string;
  accountNumber: string;
  amountSource: DecreeTemplateAmountSource;
  description: string;
}

/** Wiersz wzorca z odpowiedzi GET/POST/PATCH (`decree_templates.lines`, mirror
 * `decreeTemplates.lines.$type` w `drizzle/tenant/schema.ts`). */
export interface DecreeTemplateServerLine {
  groupNo: number;
  side: DecreeTemplateSide;
  accountNumber: string;
  amountSource: DecreeTemplateAmountSource;
  description?: string;
}

/** Kształt wysyłany w `lines[]` payloadu POST/PATCH szablonu. */
export interface DecreeTemplateLinePayload {
  groupNo: number;
  side: DecreeTemplateSide;
  accountNumber: string;
  amountSource: DecreeTemplateAmountSource;
  description?: string;
}

/** Wiersz serwera → draft edytowalny. */
export function serverTemplateLineToDraft(line: DecreeTemplateServerLine): DecreeTemplateLineDraft {
  return {
    key: crypto.randomUUID(),
    groupNo: line.groupNo,
    side: line.side,
    accountId: '',
    accountNumber: line.accountNumber,
    amountSource: line.amountSource,
    description: line.description ?? '',
  };
}

/** Draft → kształt wysyłany w `lines[]` (mirror `decreeDraftLineToPayload`). */
export function templateDraftLineToPayload(
  line: DecreeTemplateLineDraft,
): DecreeTemplateLinePayload {
  return {
    groupNo: line.groupNo,
    side: line.side,
    accountNumber: line.accountNumber,
    amountSource: line.amountSource,
    ...(line.description.trim() ? { description: line.description.trim() } : {}),
  };
}

/** Nowy pusty wiersz — domyślnie strona Wn, źródło 'gross' (najczęstszy przypadek). */
export function emptyTemplateLine(groupNo: number): DecreeTemplateLineDraft {
  return {
    key: crypto.randomUUID(),
    groupNo,
    side: 'debit',
    accountId: '',
    accountNumber: '',
    amountSource: 'gross',
    description: '',
  };
}

/** Kolejny wolny numer grupy (max istniejącego + 1; 1 gdy pusto) — mirror `nextGroupNo`
 * (`decree-lines.ts`), duplikowane celowo (typ wejściowy inny, `DecreeTemplateLineDraft`
 * nie `DecreeLineLike`, żeby nie tworzyć sztucznej wspólnej zależności między modelem
 * dekretu a modelem szablonu). */
export function nextTemplateGroupNo(lines: readonly { groupNo: number }[]): number {
  return lines.reduce((max, line) => Math.max(max, line.groupNo), 0) + 1;
}

/** Walidacja klienta przed zapisem — każdy wiersz musi mieć konto (numer) wybrane;
 * zwraca `Set<key>` wierszy niekompletnych (mirror `validateItemsDraft` w
 * `document-items-draft.ts`). */
export function validateTemplateLines(lines: readonly DecreeTemplateLineDraft[]): Set<string> {
  const invalid = new Set<string>();
  for (const line of lines) {
    if (!line.accountNumber.trim()) invalid.add(line.key);
  }
  return invalid;
}
